import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import { DataEditContextTranslationImport } from 'Components/reusable/DataContext/TranslationImport'
import CheckIcon from '@mui/icons-material/Check'
import RemoveIcon from '@mui/icons-material/Remove'
import Grid from '@mui/material/Grid2'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cvAPI } from 'api/CvAPI'
import { filesAPI } from 'api/FilesAPI'
import { personAPI } from 'api/PersonAPI'
import { urlAPI } from 'api/UrlAPI'
import { LanguagesData } from 'api/UtilsAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DataContext from 'Components/reusable/DataContext'
import CaleoIconButton from 'Components/reusable/IconButtons/CaleoIconButton'
import CloseButton from 'Components/reusable/IconButtons/CloseButton'
import { InputField } from 'Components/reusable/InputFields/InputField'
import RegularCheckbox from 'Components/reusable/InputFields/RegularCheckbox'
import { useNotificationPopup } from 'Components/reusable/Notification'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { getCvUrlInitialData } from 'Components/reusable/DataContext/InitialData'
import { cvUrlSchema, profileSchema } from 'Components/reusable/DataContext/ValidationSchema'
import AddButton from 'Components/reusable/IconButtons/AddButton'
import EditButton from 'Components/reusable/IconButtons/EditButton'
import LinkItem from './LinkItem'
import LinkModal from './LinkModal'
import ProfileModal from './ProfileModal'
import ProfileOverview from './ProfileOverview'
import { useBrokerNetworks, useCountryData, useUser } from 'hooks'
import { chooseDBTranslation } from 'utils/translations'
import { ICv, IUrl } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { CountryCode } from 'types/ids'
import { INetwork } from 'types/networkInterfaces'
import { IOrganizationTranslation } from 'types/userInterfaces'
import { About, ICvData } from 'types/profileInterfaces'

/** @notExported  */
interface IProfileCardProps {
  /** Is the editing enabled. */
  editable: boolean
  /** CV data. */
  cv: ICv
  /** Function to update the CV updated at. */
  setUpdatedAt: () => void
  /** CV data. */
  data?: ICvData
  /** Languages data for profile overview. */
  languages: LanguagesData
  /** Account networks. */
  accountNetworks?: INetwork[]
  /** Is the profile anonymous. */
  anonymous?: boolean
  /** CV public id. */
  publicId?: string
  /** Function to update the progress of CV. */
  updateProgress?: () => void
  /** Person preview. */
  personPreview?: boolean
}

/**
 * Basic details card for profile.
 *
 * @returns Component for displaying profile basic details.
 * @notExported
 */
const ProfileCard: React.FC<IProfileCardProps> = ({
  editable,
  cv,
  setUpdatedAt,
  data,
  languages,
  accountNetworks,
  anonymous = false,
  publicId,
  updateProgress,
  personPreview,
}) => {
  const { t, i18n } = useTranslation()
  const { user, groups } = useUser()

  const [modalOpen, setModalOpen] = useState(false)
  const [descriptionEditingOpen, setDescriptionEditingOpen] = useState(false)
  const [primaryRoleEditing, setPrimaryRoleEditing] = useState<boolean>(false)
  const [priceSettingsOpen, setPriceSettingsOpen] = useState(false)
  const [cvWithTranslation, setCvWithTranslation] = useState<ICv>()
  const [linkModalOpen, setLinkModalOpen] = useState(false)
  const [urls, setUrls] = useState<IUrl[]>([])
  const [url, setUrl] = useState<IUrl>()
  const { countries } = useCountryData()
  const { setNotificationPopup } = useNotificationPopup()
  const [image, setImage] = useState<string>('')
  const [about, setAbout] = useState<About>()
  const [backendError, setBackendError] = useState<IError>()
  const [role, setRole] = useState<string>()
  const [remoteWorkAvailable, setRemoteWorkAvailable] = useState<boolean>()
  const [minPrice, setMinPrice] = useState<number>(1)
  const [maxPrice, setMaxPrice] = useState<number>(1)
  const [minPriceError, setMinPriceError] = useState<string | undefined>()
  const [maxPriceError, setMaxPriceError] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [buttonLoading, setButtonLoading] = useState<boolean>(false)
  const [priceNetworks, setPriceNetworks] = useState<INetwork[]>([])
  const locale = i18n.language
  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.down('md'))
  const { networks } = useBrokerNetworks()

  let brokerNetworks: INetwork[] | undefined = undefined

  if (!personPreview) {
    brokerNetworks = networks
  }

  useEffect(() => {
    if (cvWithTranslation) {
      setRole(chooseDBTranslation(i18n, cvWithTranslation).primaryRole)
    }
  }, [cvWithTranslation, i18n, i18n.language])

  useEffect(() => {
    const controller = new AbortController()

    if (cv.id) {
      ;(async () => {
        try {
          setLoading(true)
          let aboutData
          if (publicId) {
            if (!anonymous) {
              aboutData = await cvAPI.getCvPublicIdAbout(publicId, controller)
            } else {
              aboutData = await cvAPI.getAnonymousAbout(publicId, controller)
            }
          } else {
            aboutData = await cvAPI.getCvAbout(cv.id, controller)
          }

          if (aboutData) {
            aboutData.CV = aboutData.CVs.find(currentCv => currentCv.id === cv.id) ?? aboutData.CVs[0]
            aboutData.CV.organizationId = aboutData.organizationId
            if (!aboutData.country) aboutData.country = 'fi' as CountryCode
            setAbout(aboutData)
            if (!anonymous) setCvWithTranslation(aboutData.CV)
            setRemoteWorkAvailable(aboutData.remoteWorkAvailable)
            if (aboutData.minPrice) setMinPrice(parseInt(aboutData.minPrice.toString()))
            if (aboutData.maxPrice) setMaxPrice(parseInt(aboutData.maxPrice.toString()))
          }
        } catch (error) {
          setBackendError(error as IError)
        } finally {
          setLoading(false)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [cv.id, modalOpen, publicId, anonymous])

  useEffect(() => {
    const controller = new AbortController()

    if (cv.personId) {
      ;(async () => {
        try {
          if (!anonymous) {
            if (publicId) {
              const image = await filesAPI.downloadProfilePublicId(publicId, controller)
              setImage(image)
            } else {
              const image = await personAPI.getProfileImage(cv.personId, controller)
              setImage(image)
            }
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [cv.personId, publicId, anonymous])

  useEffect(() => {
    const controller = new AbortController()

    if (cv.id && !publicId) {
      ;(async () => {
        try {
          const priceData = await cvAPI.getNetworkPriceVisible(cv.id, controller)
          setPriceNetworks(priceData)
        } catch (priceError) {
          setBackendError(priceError as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [cv.id, publicId])

  useEffect(() => {
    const controller = new AbortController()

    if (cv.id && !linkModalOpen) {
      ;(async () => {
        try {
          if (!anonymous && !publicId) {
            const results = await urlAPI.getUrls(cv.id, controller)
            setUrls(results)
          }
        } catch (error) {
          setBackendError(error as IError)
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [anonymous, cv.id, linkModalOpen, publicId])

  const ownSales =
    about &&
    user &&
    user.Groups &&
    user.Groups.find(group => group.type === 'sales' && group.organizationId === about.organizationId)
      ? true
      : false

  const showErrorNotification = () => {
    setNotificationPopup({
      message: t('error.header'),
      duration: 'long',
      type: 'error',
    })
  }

  const changeOldImage = (image: string) => {
    setImage(image)
  }

  const editUrl = (url: IUrl) => {
    setUrl(url)
    setLinkModalOpen(true)
  }

  const displayPrices = () => {
    let pricesVisible = false

    if (groups.includes('freelancer') && user?.id === about?.accountId) {
      pricesVisible = true
    } else {
      const brokerIds = brokerNetworks?.map(network => network.id)

      let broker = false
      let sales = false

      if (groups.includes('sales')) {
        sales = true
      }

      if (
        brokerIds &&
        brokerIds.length &&
        accountNetworks?.find(accountNetwork => brokerIds?.includes(accountNetwork.id))
      ) {
        broker = true
      }

      if (broker || sales) {
        pricesVisible = true
      } else {
        pricesVisible = false
      }
    }

    return pricesVisible
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (loading || !about || !about.CV)
    return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1, mx: 2, my: 1 }} />

  if (!cv) return <></>

  let organizationTranslation: IOrganizationTranslation | undefined = undefined

  if (about.Site) {
    if (about.Site.Organization) {
      organizationTranslation = chooseDBTranslation(i18n, about.Site?.Organization)
    }
  }

  const country = countries && about.country ? countries[about.country.toUpperCase()] : null
  const fullName = !anonymous
    ? `${about.firstName} ${about.lastName}`
    : role && role.length > 0
      ? role
      : t('resources.searchResults.missingRole')

  let icons: React.ReactNode[] = []
  if (urls.length > 0) {
    icons = urls.map((url, i) => {
      return <LinkItem key={i} url={url} editUrl={editUrl} />
    })
  }

  const savePrices = async () => {
    if (minPrice > maxPrice) {
      setMaxPriceError(t('error.biggerThanMin'))
    } else if (minPrice >= 0 && maxPrice >= 0) {
      try {
        await cvAPI.putPrices(cv.id, { minPrice: minPrice, maxPrice: maxPrice })
        setMinPriceError(undefined)
        setMaxPriceError(undefined)
        setPriceSettingsOpen(false)
        setUpdatedAt()
      } catch {
        showErrorNotification()
      }
    } else {
      if (minPrice < 0) {
        setMinPriceError(t('error.positive'))
      } else {
        setMinPriceError(undefined)
      }
      if (maxPrice < 0) {
        setMaxPriceError(t('error.positive'))
      } else {
        setMaxPriceError(undefined)
      }
    }
  }

  const getPriceSettings = (open: boolean) => {
    if (open) {
      return (
        <Grid>
          <Grid container direction="row" spacing={1} alignContent="center" alignItems="center" size={12}>
            <Grid>
              <InputField
                type="number"
                value={minPrice}
                onChange={e => setMinPrice(parseInt(e.target.value))}
                error={minPriceError}
                insetLabel
                label={t('profile.modal.minPrice')}
              />
            </Grid>
            <Grid>
              <RemoveIcon fontSize="small" sx={{ mt: 1 }} />
            </Grid>
            <Grid>
              <InputField
                type="number"
                value={maxPrice}
                onChange={e => setMaxPrice(parseInt(e.target.value))}
                error={maxPriceError}
                insetLabel
                label={t('profile.modal.maxPrice')}
              />
            </Grid>
            <Grid>
              <CaleoIconButton
                testId="button-profile-card-save-prices"
                icon={<CheckIcon color="success" />}
                size="xsmall"
                tooltip={t('save')}
                clickAction={savePrices}
              />
            </Grid>
          </Grid>
          <Grid container direction="row" spacing={1} size={12}>
            <Grid size={12}>{t('profile.modal.priceInfo')}</Grid>
            {priceNetworks.length > 0 && (
              <Grid size={12}>
                {t('profile.modal.priceNetwork', {
                  networks: priceNetworks.map(network => chooseDBTranslation(i18n, network).name).join(', '),
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container alignItems="center">
        {displayPrices() && (
          <>
            <Grid>
              {maxPrice > 0 || minPrice > 0 ? (
                <>
                  {minPrice} - {maxPrice} €/h
                </>
              ) : (
                <CardContentText type="priceInfo">{t('profile.about.noPrice')}</CardContentText>
              )}
            </Grid>
            {(ownSales || (groups.includes('freelancer') && user?.id === about?.accountId)) && (
              <Grid>
                <EditButton
                  testId="button-profile-card-edit-prices"
                  clickAction={() => setPriceSettingsOpen(true)}
                  size="xsmall"
                  noFloat={true}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    )
  }

  const saveDescription = async () => {
    setButtonLoading(true)
    setDescriptionEditingOpen(false)

    try {
      const translation = cvWithTranslation?.translations?.find(transl => transl?.Language?.name === locale)

      if (cvWithTranslation !== undefined && translation !== undefined) {
        await cvAPI.putDescription(cvWithTranslation.id, translation)
        {
          setUpdatedAt()
          updateProgress?.()
        }
      }
    } catch {
      showErrorNotification()
    }

    setButtonLoading(false)
  }

  const savePrimaryRole = async () => {
    try {
      if (cvWithTranslation) {
        const translation = cvWithTranslation.translations.find(transl => transl.Language.name === locale)
        setRole(translation?.primaryRole)
        await cvAPI.putRole(cvWithTranslation.id, translation)
        {
          setUpdatedAt()
          updateProgress?.()
          setPrimaryRoleEditing(false)
        }
      }
    } catch {
      showErrorNotification()
    }
  }

  const saveRemoteWork = async () => {
    try {
      await cvAPI.putRemoteWorkAvailability(cv.id, { remoteWorkAllowed: !remoteWorkAvailable })
      setUpdatedAt()
    } catch {
      showErrorNotification()
    }
    setRemoteWorkAvailable(!remoteWorkAvailable)
  }

  return (
    <>
      <Grid m="18px">
        <Grid container spacing={mobileView ? 1 : 2}>
          <Grid size={12}>
            <Grid container>
              <Grid size={12}>
                <Grid container direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Grid
                    size={{
                      xs: 12,
                      md: 7,
                    }}
                  >
                    <Grid container alignItems="center">
                      <Grid>
                        <Typography variant="h1" color="primary" fontWeight="bold">
                          {fullName}
                        </Typography>
                      </Grid>
                      <Grid>
                        {editable && (
                          <EditButton
                            testId="button-profile-card-edit-profile"
                            clickAction={() => setModalOpen(true)}
                            size="medium"
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  {displayPrices() && (
                    <Grid
                      size={{
                        xs: 12,
                        md: 5,
                      }}
                    >
                      <Grid container direction="column" alignItems="flex-end">
                        <Grid>{getPriceSettings(priceSettingsOpen)}</Grid>
                        <Grid>
                          {(ownSales || (groups.includes('freelancer') && user?.id === about?.accountId)) &&
                          remoteWorkAvailable !== undefined ? (
                            <RegularCheckbox
                              checked={remoteWorkAvailable}
                              onChange={saveRemoteWork}
                              label={t('profile.modal.remoteWorkAvailable')}
                            />
                          ) : about?.remoteWorkAvailable ? (
                            t('profile.modal.remoteWorkAvailable')
                          ) : (
                            t('profile.modal.remoteWorkNotAvailable')
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid size={12}>
                <Grid container direction="row" alignItems="center">
                  <Grid
                    size={{
                      xs: 12,
                      md: 7,
                    }}
                  >
                    <Grid container direction="row" spacing={mobileView ? 2 : 3} alignItems="center">
                      <Grid
                        size={{
                          xs: 6,
                          sm: undefined,
                        }}
                      >
                        <Avatar
                          src={image}
                          sx={{ width: mobileView ? 150 : 200, height: mobileView ? 150 : 200 }}
                          alt={fullName}
                          role="img"
                          aria-label={fullName}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 6,
                          sm: undefined,
                        }}
                      >
                        <Grid container spacing={1} direction="column" alignItems="flex-start">
                          <Grid>{organizationTranslation && organizationTranslation?.name}</Grid>
                          <Grid>
                            {cvWithTranslation && primaryRoleEditing ? (
                              <Grid container alignItems="center">
                                <Grid size={8}>
                                  <DataContext
                                    data={cvWithTranslation}
                                    onChange={newData => setCvWithTranslation(newData)}
                                    localeBase="profile.modal"
                                  >
                                    <DataEditContextTranslationImport<ICv> />
                                    <DataEditContextTextField<ICv> field="primaryRole" fullWidth hideLabel />
                                  </DataContext>
                                </Grid>
                                <Grid>
                                  <CloseButton
                                    testId="button-profile-card-cancel-primary-role"
                                    clickAction={() => setPrimaryRoleEditing(false)}
                                    size="small"
                                  />
                                  <CaleoIconButton
                                    testId="button-profile-card-save-primary-role"
                                    icon={<CheckIcon color="success" />}
                                    size="xsmall"
                                    tooltip={t('save')}
                                    clickAction={savePrimaryRole}
                                  />
                                </Grid>
                              </Grid>
                            ) : (
                              <Grid container justifyContent="flex-start" alignItems="center">
                                {!anonymous && (
                                  <Typography fontSize={26} fontWeight="bold" style={{ wordBreak: 'break-word' }}>
                                    {role && role?.length > 0 ? role : t('noRoleSet')}
                                  </Typography>
                                )}
                                {editable && !loading && (
                                  <EditButton
                                    testId="button-profile-card-edit-primary-role"
                                    clickAction={() => setPrimaryRoleEditing(true)}
                                    size="xsmall"
                                    noFloat={true}
                                  />
                                )}
                              </Grid>
                            )}
                          </Grid>
                          <Grid>{about.telephone}</Grid>
                          <Grid>
                            <Grid lineHeight="17px" fontSize="18px">
                              {about.postalCode} {about.city}
                              {about.city && country && ','} {country}
                            </Grid>
                          </Grid>
                          {!anonymous && (
                            <Grid>
                              <Grid container alignItems="center">
                                <Grid>{icons}</Grid>
                                <Tooltip title={t('profile.about.addLinkTooltip')}>
                                  <Grid>
                                    {editable && (
                                      <AddButton
                                        testId="button-profile-card-add-link"
                                        clickAction={() => setLinkModalOpen(true)}
                                        size="xsmall"
                                      />
                                    )}
                                  </Grid>
                                </Tooltip>
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <ProfileOverview data={data} languages={languages} publicId={publicId} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {!anonymous && (
            <Grid size={12}>
              {descriptionEditingOpen && cvWithTranslation ? (
                <Grid container justifyContent="flex-end">
                  <Grid size={12}>
                    <DataContext
                      data={cvWithTranslation}
                      onChange={newData => setCvWithTranslation(newData)}
                      localeBase="profile.modal"
                    >
                      <DataEditContextTranslationImport<ICv> />
                      <DataEditContextTextField<ICv>
                        field="about"
                        fullWidth
                        rows={10}
                        multiline
                        maxHintLength={1500}
                        grow
                        insetLabel
                      />
                    </DataContext>
                  </Grid>
                  <Grid>
                    <Button
                      data-testid="button-profile-save-description"
                      onClick={saveDescription}
                      color="primary"
                      variant="contained"
                      disabled={buttonLoading}
                    >
                      {t('save')}
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <CardContentText type="profileDescription">
                  <p style={{ display: 'inline' }}>{chooseDBTranslation(i18n, cvWithTranslation)?.about}</p>
                  <div style={{ display: 'inline', marginLeft: 10 }}>
                    {editable && (
                      <EditButton
                        testId="button-profile-card-edit-description"
                        clickAction={() => setDescriptionEditingOpen(true)}
                        size="xsmall"
                        noFloat={true}
                      />
                    )}
                  </div>
                </CardContentText>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      {modalOpen && (
        <ProfileModal
          item={about}
          schema={profileSchema()}
          maxWidth="md"
          fullWidth
          localeBase={'profile.modal'}
          cvId={cv.id}
          countries={countries}
          oldImage={image}
          personId={cv.personId}
          changeOldImage={changeOldImage}
          onClose={() => {
            setUpdatedAt()
            setModalOpen(false)
          }}
          disableDelete={true}
          submitOnModal={true}
          header="about"
          noEscClose
        />
      )}
      {linkModalOpen && (
        <LinkModal
          onClose={(value?: Record<string, unknown> | { id?: number; name?: string } | { newItem?: unknown }) => {
            const newItem = (value as { newItem?: unknown } | undefined)?.newItem as IUrl | undefined
            setLinkModalOpen(false)
            setUrl(undefined)
            if (newItem) {
              setUpdatedAt()
            }
          }}
          item={url}
          cvId={cv.id}
          initialData={getCvUrlInitialData(cv.id)}
          schema={cvUrlSchema()}
          api={{
            create: (cvId: number, item: unknown) => urlAPI.create(cvId, item),
            save: (item: IUrl, data: unknown) => urlAPI.save(item, data),
            delete: (id: number, cvId?: number) => urlAPI.delete(id, cvId ?? cv.id),
          }}
          localeBase="url.modal"
          maxWidth="xs"
          fullWidth={true}
          noEscClose
        />
      )}
    </>
  )
}

export default ProfileCard
